import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { api } from '../../services/api';

function comprimirImagem(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 350;
        let { width, height } = img;
        if (width > MAX) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatarHorario(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

function toDatetimeLocal(iso) {
  if (!iso) return '';
  // Horários são stored como UTC naive — usa getUTC* para não converter fuso.
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

function StatusEvento({ status }) {
  const map = {
    PUBLISHED: { label: 'Publicado', bg: '#dcfce7', color: '#166534' },
    DRAFT: { label: 'Rascunho', bg: '#fef9c3', color: '#854d0e' },
    ENDED: { label: 'Encerrado', bg: '#f1f5f9', color: '#64748b' },
    CANCELLED: { label: 'Cancelado', bg: '#fee2e2', color: '#991b1b' },
    ARCHIVED: { label: 'Arquivado', bg: '#f3f4f6', color: '#6b7280' },
  };
  const s = map[status] ?? { label: status, bg: '#f1f5f9', color: '#64748b' };
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: '0.08em',
        padding: '3px 10px',
        borderRadius: 99,
        background: s.bg,
        color: s.color,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

function ModalShell({ title, onClose, maxWidth = 480, children }) {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        className="ge-modal"
        style={{ ...styles.modal, maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            style={styles.btnClose}
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormaPagamentoField({ pixOnly, onChange }) {
  const opcoes = [
    {
      valor: false,
      titulo: 'PIX e Cartão de Crédito',
      descricao: 'A leitora escolhe como pagar (padrão)',
    },
    {
      valor: true,
      titulo: 'Somente PIX',
      descricao: 'Esconde a opção de cartão no checkout',
    },
  ];
  return (
    <div>
      <p style={styles.label}>Forma de pagamento aceita</p>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginTop: 6,
        }}
      >
        {opcoes.map((op) => (
          <label
            key={String(op.valor)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '10px 14px',
              border: `1px solid ${pixOnly === op.valor ? '#ff2d6c' : '#e2e8f0'}`,
              borderRadius: 10,
              cursor: 'pointer',
              background: pixOnly === op.valor ? '#fff5f7' : '#fff',
            }}
          >
            <input
              type="radio"
              name="formaPagamento"
              checked={pixOnly === op.valor}
              onChange={() => onChange(op.valor)}
              style={{ marginTop: 3 }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                {op.titulo}
              </div>
              <div style={{ fontSize: 11.5, color: '#94a3b8' }}>
                {op.descricao}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

/** Estado inicial do modal de criação. Fora do componente para que abrir
 *  e resetar o formulário partam exatamente do mesmo lugar. */
const EVENTO_EM_BRANCO = {
  title: '',
  mesReferencia: '',
  local: 'Plataforma Online / Google Meet',
  amount: 89.9,
  memberAmount: 64.99,
  pixOnly: false,
  bookTitle: '',
  bookAuthor: '',
  bookCoverBase64: '',
};

/**
 * Livro do mês — capa, título e autora.
 *
 * Mora nos dois modais, criar e editar. Antes só existia no de editar:
 * para pôr a capa era preciso salvar o evento, reabrir em "Editar" e só
 * então anexar — e é essa capa que a Home mostra na próxima edição.
 *
 * A prévia sai do próprio valor, não de um estado à parte: duas fontes
 * para a mesma capa só davam chance de saírem do ar uma da outra.
 */
function CampoLivroDoMes({ valores, onChange, onErro }) {
  const fileRef = useRef();
  const capa = valores.bookCoverBase64;

  async function selecionarCapa(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      onChange({ bookCoverBase64: await comprimirImagem(file) });
    } catch {
      onErro('Falha ao processar imagem.');
    } finally {
      // Sem limpar, reescolher o mesmo arquivo não dispara change de novo.
      e.target.value = '';
    }
  }

  return (
    <div
      style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14, marginTop: 4 }}
    >
      <p
        style={{
          fontSize: 11,
          fontWeight: 900,
          color: '#64748b',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 12,
        }}
      >
        Livro do mês (exibido na home)
      </p>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            width: 90,
            aspectRatio: '3/4',
            borderRadius: 12,
            border: '2px dashed #e2e8f0',
            background: '#f8fafc',
            cursor: 'pointer',
            overflow: 'hidden',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {capa ? (
            <img
              src={capa}
              alt="Capa"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span
              style={{
                fontSize: 10,
                color: '#94a3b8',
                textAlign: 'center',
                padding: 8,
              }}
            >
              Clique para enviar capa
            </span>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={selecionarCapa}
        />

        <div
          style={{
            flex: '1 1 180px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <label style={styles.label}>
            Título do livro
            <input
              style={styles.input}
              value={valores.bookTitle}
              onChange={(e) => onChange({ bookTitle: e.target.value })}
              placeholder="Ex: Quarta Asa"
            />
          </label>
          <label style={styles.label}>
            Autora
            <input
              style={styles.input}
              value={valores.bookAuthor}
              onChange={(e) => onChange({ bookAuthor: e.target.value })}
              placeholder="Ex: Rebecca Yarros"
            />
          </label>
        </div>
      </div>
      {capa && (
        <button
          type="button"
          onClick={() => onChange({ bookCoverBase64: '' })}
          style={{
            marginTop: 8,
            fontSize: 11,
            color: '#dc2626',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          Remover capa
        </button>
      )}
    </div>
  );
}

export default function GestaoEventos() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [expandido, setExpandido] = useState(null);

  // Modal novo evento
  const [modalEvento, setModalEvento] = useState(false);
  const [novoEvento, setNovoEvento] = useState(EVENTO_EM_BRANCO);

  // Modal novo horário
  const [modalDia, setModalDia] = useState(null);
  const [novoDia, setNovoDia] = useState({ dataInicio: '', vagas: null });

  // Modal editar horário
  const [modalEditarDia, setModalEditarDia] = useState(null);
  const [editDiaForm, setEditDiaForm] = useState({ dataInicio: '', vagas: '' });

  // Modal editar evento
  const [modalEditar, setModalEditar] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    local: '',
    dataInicio: '',
    dataFim: '',
    amount: 0,
    memberAmount: 0,
    habilitarMember: true,
    socialAmount: 0,
    habilitarSocial: true,
    bookTitle: '',
    bookAuthor: '',
    bookCoverBase64: '',
    pixOnly: false,
    habilitarExtra: false,
    nomeExtra: '',
    descricaoExtra: '',
    valorExtra: 0,
    addonId: null,
  });
  // Filtro de eventos
  const [filtroStatus, setFiltroStatus] = useState('ativos');

  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState(null);

  async function carregar() {
    try {
      setCarregando(true);
      const { data } = await api.get('/admin/eventos');
      setEventos(data);
    } catch {
      setErro('Erro ao carregar eventos.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  function mostrarFeedback(msg, tipo = 'ok') {
    setFeedback({ msg, tipo });
    setTimeout(() => setFeedback(null), 3500);
  }

  async function toggleStatus(evento) {
    const novoStatus = evento.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';

    if (novoStatus === 'PUBLISHED' && evento.dias.length === 0) {
      mostrarFeedback(
        'Adicione pelo menos um horário antes de publicar o evento.',
        'erro',
      );
      setModalDia(evento.id);
      return;
    }

    try {
      const { data } = await api.patch(`/admin/eventos/${evento.id}`, {
        status: novoStatus,
      });

      if (novoStatus === 'PUBLISHED' && data.autoEnroll != null) {
        const { inscritas, semDia, semCpf } = data.autoEnroll;
        const partes = [];
        if (inscritas > 0)
          partes.push(`${inscritas} Lily Member(s) inscritas automaticamente`);
        if (semDia > 0) partes.push(`${semDia} sem horário compatível`);
        if (semCpf > 0) partes.push(`${semCpf} sem cadastro completo`);
        mostrarFeedback(
          `Evento publicado! ${partes.length > 0 ? partes.join(' · ') : 'Nenhuma Lily Member ativa para inscrever.'}`,
        );
      } else {
        mostrarFeedback(
          `Evento ${novoStatus === 'PUBLISHED' ? 'publicado' : 'voltado para rascunho'}!`,
        );
      }

      carregar();
    } catch {
      mostrarFeedback('Erro ao atualizar status.', 'erro');
    }
  }

  async function criarEvento(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.post('/admin/eventos', {
        ...novoEvento,
        amountInCents: Math.round(parseFloat(novoEvento.amount) * 100),
        memberAmountInCents: Math.round(
          parseFloat(novoEvento.memberAmount) * 100,
        ),
        bookTitle: novoEvento.bookTitle || null,
        bookAuthor: novoEvento.bookAuthor || null,
        bookCoverBase64: novoEvento.bookCoverBase64 || null,
      });
      setModalEvento(false);
      setNovoEvento(EVENTO_EM_BRANCO);
      mostrarFeedback('Evento criado com sucesso!');
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao criar evento.',
        'erro',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function adicionarDia(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.post(`/admin/eventos/${modalDia}/dias`, novoDia);
      setModalDia(null);
      setNovoDia({ dataInicio: '', vagas: null });
      mostrarFeedback('Horário adicionado!');
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao adicionar horário.',
        'erro',
      );
    } finally {
      setSalvando(false);
    }
  }

  function abrirEditar(evento) {
    const primeiroAddOn = evento.addOns?.[0];
    const memberValue = (evento.memberAmountInCents / 100).toFixed(2);
    setEditForm({
      title: evento.title,
      local: evento.location,
      dataInicio: toDatetimeLocal(evento.startDate),
      dataFim: toDatetimeLocal(evento.endDate),
      amount: (evento.amountInCents / 100).toFixed(2),
      memberAmount: memberValue,
      habilitarMember: parseFloat(memberValue) > 0,
      socialAmount: ((evento.socialAmountInCents ?? 0) / 100).toFixed(2),
      habilitarSocial: (evento.socialAmountInCents ?? 0) > 0,
      bookTitle: evento.bookTitle || '',
      bookAuthor: evento.bookAuthor || '',
      bookCoverBase64: evento.bookCoverBase64 || '',
      pixOnly: evento.pixOnly || false,
      habilitarExtra: !!primeiroAddOn,
      nomeExtra: primeiroAddOn?.name || '',
      descricaoExtra: primeiroAddOn?.description || '',
      valorExtra: primeiroAddOn
        ? parseFloat((primeiroAddOn.priceInCents / 100).toFixed(2))
        : 0,
      addonId: primeiroAddOn?.id || null,
    });
    setModalEditar(evento.id);
  }

  async function salvarEditar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.patch(`/admin/eventos/${modalEditar}`, {
        title: editForm.title,
        local: editForm.local,
        dataInicio: editForm.dataInicio || undefined,
        dataFim: editForm.dataFim || undefined,
        amountInCents: Math.round(parseFloat(editForm.amount) * 100),
        memberAmountInCents:
          editForm.habilitarMember !== false
            ? Math.round(parseFloat(editForm.memberAmount) * 100)
            : 0,
        socialAmountInCents: editForm.habilitarSocial
          ? Math.round(parseFloat(editForm.socialAmount) * 100)
          : 0,
        bookTitle: editForm.bookTitle || null,
        bookAuthor: editForm.bookAuthor || null,
        bookCoverBase64: editForm.bookCoverBase64 || null,
        pixOnly: editForm.pixOnly,
      });

      // Salvar add-ons - GARANTE apenas 1 add-on por evento
      if (
        editForm.habilitarExtra &&
        editForm.nomeExtra &&
        editForm.valorExtra > 0
      ) {
        // SEMPRE deletar TODOS os add-ons antigos antes de criar novo
        console.log('[ADDON DEBUG] Deletando TODOS os add-ons antigos...');
        try {
          const allAddons = await api.get(
            `/admin/eventos/${modalEditar}/addons`,
          );
          for (const addon of allAddons.data || []) {
            await api.delete(
              `/admin/eventos/${modalEditar}/addons/${addon.id}`,
            );
            console.log('[ADDON DEBUG] Deletado:', addon.id);
          }
        } catch (err) {
          console.log(
            '[ADDON DEBUG] Erro ao limpar antigos (continuando...):',
            err.message,
          );
        }

        const priceInCents = Math.round(parseFloat(editForm.valorExtra) * 100);

        // Criar NOVO add-on (único)
        console.log('[ADDON DEBUG] Criando novo add-on único:', {
          name: editForm.nomeExtra,
          description: editForm.descricaoExtra,
          priceInCents,
        });
        const response = await api.post(
          `/admin/eventos/${modalEditar}/addons`,
          {
            name: editForm.nomeExtra,
            description: editForm.descricaoExtra,
            priceInCents,
          },
        );
        console.log('[ADDON DEBUG] ✅ Add-on criado:', response.data);
      } else if (!editForm.habilitarExtra) {
        // DESABILITAR: Deletar TODOS os add-ons
        console.log(
          '[ADDON DEBUG] Desabilitando - Deletando TODOS os add-ons...',
        );
        try {
          const allAddons = await api.get(
            `/admin/eventos/${modalEditar}/addons`,
          );
          for (const addon of allAddons.data || []) {
            await api.delete(
              `/admin/eventos/${modalEditar}/addons/${addon.id}`,
            );
            console.log('[ADDON DEBUG] ✅ Deletado:', addon.id);
          }
        } catch (err) {
          console.log(
            '[ADDON DEBUG] Erro ao deletar (continuando...):',
            err.message,
          );
        }
      }

      setModalEditar(null);
      mostrarFeedback('Evento atualizado!');
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao atualizar evento.',
        'erro',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function excluirEvento(evento) {
    const aviso =
      evento.totalInscricoes > 0
        ? `⚠️ ATENÇÃO: Este evento tem ${evento.totalInscricoes} inscrição(ões).\n\nAo excluir, o evento some da listagem mas os dados das inscritas permanecem no banco.\n\nDeseja continuar?`
        : `Excluir "${evento.title}"? Esta ação não pode ser desfeita.`;
    if (!confirm(aviso)) return;
    try {
      await api.delete(`/admin/eventos/${evento.id}`);
      mostrarFeedback('Evento excluído.');
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao excluir evento.',
        'erro',
      );
    }
  }

  function abrirEditarDia(dia) {
    setEditDiaForm({
      dataInicio: toDatetimeLocal(dia.startDate),
      vagas: dia.slots ?? '',
    });
    setModalEditarDia(dia.id);
  }

  async function salvarEditarDia(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.patch(`/admin/dias/${modalEditarDia}`, {
        dataInicio: editDiaForm.dataInicio,
        vagas: editDiaForm.vagas === '' ? null : Number(editDiaForm.vagas),
      });
      setModalEditarDia(null);
      mostrarFeedback('Horário atualizado!');
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao atualizar horário.',
        'erro',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function removerDia(diaId, inscricoes = 0) {
    const temInscritos = inscricoes > 0;
    const msg = temInscritos
      ? `⚠️ ATENÇÃO: Este horário tem ${inscricoes} inscrição(ões).\n\nAo remover, as inscritas NÃO serão apagadas do banco, apenas o horário será removido.\n\nDeseja continuar?`
      : 'Remover este horário?';

    if (!confirm(msg)) return;

    try {
      await api.delete(`/admin/dias/${diaId}`, {
        data: { forcar: temInscritos },
      });
      mostrarFeedback('Horário removido.');
      carregar();
    } catch (err) {
      const erro = err.response?.data;
      mostrarFeedback(
        erro?.mensagem || erro?.error || 'Erro ao remover horário.',
        'erro',
      );
    }
  }

  if (carregando)
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 64 }}>
          Carregando...
        </div>
      </AdminLayout>
    );

  return (
    <AdminLayout>
      <style>{`
        .ge-card-header { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap; }
        .ge-card-titlebox { display:flex; align-items:flex-start; gap:10px; min-width:0; flex:1 1 220px; }
        .ge-actions { display:flex; gap:8px; flex-wrap:wrap; }
        .ge-actions button { white-space:nowrap; }
        .ge-dia-table { display:table; width:100%; }
        .ge-dia-cards { display:none; }
        @media (max-width: 680px) {
          .ge-card { padding:16px !important; }
          .ge-card-titlebox { flex: 1 1 100%; }
          .ge-actions { width:100%; }
          .ge-actions button { flex:1 1 calc(50% - 4px); justify-content:center; }
          .ge-dia-table { display:none; }
          .ge-dia-cards { display:flex; flex-direction:column; gap:10px; }
          .ge-modal { padding:20px !important; border-radius:16px !important; max-width: calc(100vw - 24px) !important; }
        }
      `}</style>

      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Eventos</h1>
          <p style={styles.subtitle}>
            Gerencie eventos mensais e veja histórico de eventos passados
          </p>
        </div>
        {filtroStatus === 'ativos' && (
          <button
            onClick={() => setModalEvento(true)}
            style={styles.btnPrimary}
          >
            <Plus size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
            Novo Mês
          </button>
        )}
      </div>

      {erro && <div style={styles.erro}>{erro}</div>}

      {feedback && (
        <div
          style={{
            ...styles.feedback,
            background: feedback.tipo === 'erro' ? '#fee2e2' : '#dcfce7',
            color: feedback.tipo === 'erro' ? '#991b1b' : '#166534',
          }}
        >
          {feedback.msg}
        </div>
      )}

      {filtroStatus === 'arquivados' && (
        <div
          style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            fontSize: 13,
            color: '#166534',
            lineHeight: 1.5,
          }}
        >
          📚 <strong>Histórico de Eventos</strong> — Eventos que já ocorreram.
          Todos os registros de inscrição são preservados. Clique em
          "Inscrições" para ver quem participou.
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 24,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <button
          onClick={() => setFiltroStatus('ativos')}
          style={{
            flex: '0 1 auto',
            background: 'none',
            border: 'none',
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: filtroStatus === 'ativos' ? 700 : 500,
            color: filtroStatus === 'ativos' ? '#0f172a' : '#94a3b8',
            cursor: 'pointer',
            borderBottom:
              filtroStatus === 'ativos' ? '3px solid #059669' : 'none',
            transition: 'all 150ms ease',
          }}
        >
          Gerenciar Eventos
        </button>
        <button
          onClick={() => setFiltroStatus('arquivados')}
          style={{
            flex: '0 1 auto',
            background: 'none',
            border: 'none',
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: filtroStatus === 'arquivados' ? 700 : 500,
            color: filtroStatus === 'arquivados' ? '#0f172a' : '#94a3b8',
            cursor: 'pointer',
            borderBottom:
              filtroStatus === 'arquivados' ? '3px solid #6b7280' : 'none',
            transition: 'all 150ms ease',
          }}
        >
          📋 Histórico (Passados)
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {eventos
          .filter((e) => {
            if (filtroStatus === 'arquivados') return e.status === 'ARCHIVED';
            return e.status !== 'ARCHIVED';
          })
          .map((evento) => {
            const aberto = expandido === evento.id;
            return (
              <div key={evento.id} className="ge-card" style={styles.card}>
                <div className="ge-card-header">
                  <div className="ge-card-titlebox">
                    <button
                      onClick={() => setExpandido(aberto ? null : evento.id)}
                      style={styles.btnIcone}
                      aria-label={aberto ? 'Recolher horários' : 'Ver horários'}
                    >
                      {aberto ? (
                        <ChevronDown size={18} />
                      ) : (
                        <ChevronRight size={18} />
                      )}
                    </button>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 900,
                            fontSize: 15,
                            color: '#0f172a',
                          }}
                        >
                          {evento.title}
                        </span>
                        <StatusEvento status={evento.status} />
                        {evento.pixOnly && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 900,
                              letterSpacing: '0.08em',
                              padding: '3px 10px',
                              borderRadius: 99,
                              background: '#dbeafe',
                              color: '#1e40af',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            SOMENTE PIX
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: '#94a3b8',
                          marginTop: 3,
                          lineHeight: 1.4,
                        }}
                      >
                        {evento.dias.length} horário(s) ·{' '}
                        {evento.totalInscricoes} inscrita(s)
                        <br />
                        {evento.location}
                      </div>

                      {/* Preços com layout melhorado */}
                      <div
                        style={{
                          marginTop: 12,
                          padding: '10px 12px',
                          background: '#f8fafc',
                          borderRadius: 8,
                          display: 'flex',
                          gap: 20,
                          flexWrap: 'wrap',
                          fontSize: 12,
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              color: '#94a3b8',
                              fontWeight: 600,
                              fontSize: 10,
                              marginBottom: 3,
                            }}
                          >
                            LILY PASS
                          </div>
                          <span
                            style={{
                              color: '#0f172a',
                              fontWeight: 800,
                              fontSize: 13,
                            }}
                          >
                            R${' '}
                            {(evento.amountInCents / 100)
                              .toFixed(2)
                              .replace('.', ',')}
                          </span>
                        </div>
                        <div>
                          <div
                            style={{
                              color: '#94a3b8',
                              fontWeight: 600,
                              fontSize: 10,
                              marginBottom: 3,
                            }}
                          >
                            LILY MEMBER
                          </div>
                          <span
                            style={{
                              color: '#0f172a',
                              fontWeight: 800,
                              fontSize: 13,
                            }}
                          >
                            R${' '}
                            {(evento.memberAmountInCents / 100)
                              .toFixed(2)
                              .replace('.', ',')}
                          </span>
                        </div>
                        {evento.socialAmountInCents > 0 && (
                          <div>
                            <div
                              style={{
                                color: '#10b981',
                                fontWeight: 600,
                                fontSize: 10,
                                marginBottom: 3,
                              }}
                            >
                              INGRESSO SOCIAL
                            </div>
                            <span
                              style={{
                                color: '#10b981',
                                fontWeight: 800,
                                fontSize: 13,
                              }}
                            >
                              R${' '}
                              {(evento.socialAmountInCents / 100)
                                .toFixed(2)
                                .replace('.', ',')}
                            </span>
                          </div>
                        )}
                        {evento.addOns &&
                          evento.addOns.length > 0 &&
                          evento.addOns[0].active && (
                            <div
                              style={{
                                padding: '8px 12px',
                                background: '#fff5f7',
                                borderRadius: 6,
                                borderLeft: '3px solid #ff2d6c',
                              }}
                            >
                              <div
                                style={{
                                  color: '#ff2d6c',
                                  fontWeight: 700,
                                  fontSize: 10,
                                  marginBottom: 3,
                                }}
                              >
                                ✦ VALOR EXTRA
                              </div>
                              <div
                                style={{
                                  color: '#0f172a',
                                  fontWeight: 800,
                                  fontSize: 13,
                                }}
                              >
                                R${' '}
                                {(evento.addOns[0].priceInCents / 100)
                                  .toFixed(2)
                                  .replace('.', ',')}
                              </div>
                              <div
                                style={{
                                  color: '#94a3b8',
                                  fontSize: 10,
                                  marginTop: 4,
                                }}
                              >
                                {evento.addOns[0].name}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="ge-actions">
                    <button
                      onClick={() => navigate(`/admin/inscricoes/${evento.id}`)}
                      style={styles.btnSecondary}
                    >
                      <Users
                        size={13}
                        style={{ marginRight: 5, verticalAlign: -2 }}
                      />
                      Inscrições
                    </button>
                    <button
                      onClick={() => abrirEditar(evento)}
                      style={styles.btnSecondary}
                    >
                      <Pencil
                        size={13}
                        style={{ marginRight: 5, verticalAlign: -2 }}
                      />
                      Editar
                    </button>
                    <button
                      onClick={() => toggleStatus(evento)}
                      style={{
                        ...styles.btnSecondary,
                        color:
                          evento.status === 'PUBLISHED' ? '#854d0e' : '#166534',
                      }}
                    >
                      {evento.status === 'PUBLISHED' ? (
                        <EyeOff
                          size={13}
                          style={{ marginRight: 5, verticalAlign: -2 }}
                        />
                      ) : (
                        <Eye
                          size={13}
                          style={{ marginRight: 5, verticalAlign: -2 }}
                        />
                      )}
                      {evento.status === 'PUBLISHED'
                        ? 'Despublicar'
                        : 'Publicar'}
                    </button>
                    <button
                      onClick={() => setModalDia(evento.id)}
                      style={styles.btnPrimary}
                    >
                      <Plus
                        size={13}
                        style={{ marginRight: 5, verticalAlign: -2 }}
                      />
                      Horário
                    </button>
                    <button
                      onClick={() => excluirEvento(evento)}
                      style={{
                        ...styles.btnSecondary,
                        color: '#dc2626',
                        borderColor: '#fecaca',
                      }}
                    >
                      <Trash2
                        size={13}
                        style={{ marginRight: 5, verticalAlign: -2 }}
                      />
                      Excluir
                    </button>
                  </div>
                </div>

                {aberto && (
                  <div
                    style={{
                      marginTop: 16,
                      borderTop: '1px solid #f1f5f9',
                      paddingTop: 16,
                    }}
                  >
                    {evento.dias.length === 0 ? (
                      <p style={{ color: '#94a3b8', fontSize: 13 }}>
                        Nenhum horário cadastrado.
                      </p>
                    ) : (
                      <>
                        {/* Desktop: tabela */}
                        <div style={{ overflowX: 'auto' }}>
                          <table className="ge-dia-table" style={styles.table}>
                            <thead>
                              <tr>
                                {[
                                  'Data e Horário',
                                  'Vagas',
                                  'Ocupadas',
                                  'Inscrições',
                                  'Ação',
                                ].map((h) => (
                                  <th key={h} style={styles.th}>
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {evento.dias.map((dia) => (
                                <tr key={dia.id} style={styles.tr}>
                                  <td style={styles.td}>
                                    {formatarHorario(dia.startDate)}
                                  </td>
                                  <td style={styles.td}>
                                    {dia.slots ?? 'Ilimitado'}
                                  </td>
                                  <td style={styles.td}>{dia.occupiedSlots}</td>
                                  <td style={styles.td}>{dia.inscricoes}</td>
                                  <td
                                    style={{
                                      ...styles.td,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <button
                                      onClick={() => abrirEditarDia(dia)}
                                      style={styles.btnLinkAzul}
                                    >
                                      Editar
                                    </button>
                                    <button
                                      onClick={() =>
                                        removerDia(dia.id, dia.inscricoes)
                                      }
                                      style={{
                                        ...styles.btnLinkVermelho,
                                        marginLeft: 12,
                                      }}
                                    >
                                      Remover
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile: cards empilhados */}
                        <div className="ge-dia-cards">
                          {evento.dias.map((dia) => (
                            <div key={dia.id} style={styles.diaCard}>
                              <div
                                style={{
                                  fontWeight: 800,
                                  fontSize: 13.5,
                                  color: '#0f172a',
                                }}
                              >
                                {formatarHorario(dia.startDate)}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 6,
                                  flexWrap: 'wrap',
                                  marginTop: 8,
                                }}
                              >
                                <span style={styles.pill}>
                                  Vagas: {dia.slots ?? 'Ilimitado'}
                                </span>
                                <span style={styles.pill}>
                                  Ocupadas: {dia.occupiedSlots}
                                </span>
                                <span style={styles.pill}>
                                  Inscrições: {dia.inscricoes}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 16,
                                  marginTop: 10,
                                  borderTop: '1px solid #f1f5f9',
                                  paddingTop: 10,
                                }}
                              >
                                <button
                                  onClick={() => abrirEditarDia(dia)}
                                  style={styles.btnLinkAzul}
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() =>
                                    removerDia(dia.id, dia.inscricoes)
                                  }
                                  style={styles.btnLinkVermelho}
                                >
                                  Remover
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Modal: Novo Evento */}
      {modalEvento && (
        <ModalShell
          title="Criar novo mês de evento"
          onClose={() => setModalEvento(false)}
        >
          <form
            onSubmit={criarEvento}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <label style={styles.label}>
              Título
              <input
                required
                style={styles.input}
                value={novoEvento.title}
                onChange={(e) =>
                  setNovoEvento({ ...novoEvento, title: e.target.value })
                }
                placeholder="Ex: Encontro de Agosto 2026"
              />
            </label>
            <label style={styles.label}>
              Mês de referência (YYYY-MM)
              <input
                required
                type="month"
                style={styles.input}
                value={novoEvento.mesReferencia}
                onChange={(e) =>
                  setNovoEvento({
                    ...novoEvento,
                    mesReferencia: e.target.value,
                  })
                }
              />
            </label>
            <label style={styles.label}>
              Local
              <input
                required
                style={styles.input}
                value={novoEvento.local}
                onChange={(e) =>
                  setNovoEvento({ ...novoEvento, local: e.target.value })
                }
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Lily Pass (R$)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={styles.input}
                  value={novoEvento.amount}
                  onChange={(e) =>
                    setNovoEvento({ ...novoEvento, amount: e.target.value })
                  }
                  placeholder="89.90"
                />
              </label>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Lily Member (R$)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={styles.input}
                  value={novoEvento.memberAmount}
                  onChange={(e) =>
                    setNovoEvento({
                      ...novoEvento,
                      memberAmount: e.target.value,
                    })
                  }
                  placeholder="64.99"
                />
              </label>
            </div>
            <FormaPagamentoField
              pixOnly={novoEvento.pixOnly}
              onChange={(v) => setNovoEvento({ ...novoEvento, pixOnly: v })}
            />
            <CampoLivroDoMes
              valores={novoEvento}
              onChange={(patch) => setNovoEvento((f) => ({ ...f, ...patch }))}
              onErro={(msg) => mostrarFeedback(msg, 'erro')}
            />
            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setModalEvento(false)}
                style={styles.btnSecondary}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={salvando}
                style={styles.btnPrimary}
              >
                {salvando ? 'Salvando...' : 'Criar evento'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {/* Modal: Editar Evento */}
      {modalEditar && (
        <ModalShell
          title="Editar evento"
          onClose={() => setModalEditar(null)}
          maxWidth={540}
        >
          <form
            onSubmit={salvarEditar}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <label style={styles.label}>
              Título
              <input
                required
                style={styles.input}
                value={editForm.title}
                onChange={(e) =>
                  setEditForm({ ...editForm, title: e.target.value })
                }
              />
            </label>
            <label style={styles.label}>
              Local
              <input
                required
                style={styles.input}
                value={editForm.local}
                onChange={(e) =>
                  setEditForm({ ...editForm, local: e.target.value })
                }
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Início
                <input
                  type="datetime-local"
                  style={styles.input}
                  value={editForm.dataInicio}
                  onChange={(e) =>
                    setEditForm({ ...editForm, dataInicio: e.target.value })
                  }
                />
              </label>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Fim
                <input
                  type="datetime-local"
                  style={styles.input}
                  value={editForm.dataFim}
                  onChange={(e) =>
                    setEditForm({ ...editForm, dataFim: e.target.value })
                  }
                />
              </label>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Lily Pass (R$)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={styles.input}
                  value={editForm.amount}
                  onChange={(e) =>
                    setEditForm({ ...editForm, amount: e.target.value })
                  }
                  placeholder="89.90"
                />
              </label>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Lily Member (R$)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={styles.input}
                  value={editForm.memberAmount}
                  onChange={(e) =>
                    setEditForm({ ...editForm, memberAmount: e.target.value })
                  }
                  placeholder="64.99"
                />
              </label>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Social (R$)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={styles.input}
                  value={editForm.socialAmount}
                  onChange={(e) =>
                    setEditForm({ ...editForm, socialAmount: e.target.value })
                  }
                  placeholder="30.00"
                />
              </label>
            </div>
            <FormaPagamentoField
              pixOnly={editForm.pixOnly}
              onChange={(v) => setEditForm({ ...editForm, pixOnly: v })}
            />

            <CampoLivroDoMes
              valores={editForm}
              onChange={(patch) => setEditForm((f) => ({ ...f, ...patch }))}
              onErro={(msg) => mostrarFeedback(msg, 'erro')}
            />

            {/* Lily Member e Ingresso Social - Toggles */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                marginTop: 14,
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                <input
                  type="checkbox"
                  checked={editForm.habilitarMember !== false}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      habilitarMember: e.target.checked,
                    })
                  }
                  style={{ cursor: 'pointer', width: 18, height: 18 }}
                />
                <span style={{ fontWeight: 600 }}>Habilitar Lily Member</span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                <input
                  type="checkbox"
                  checked={editForm.habilitarSocial}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      habilitarSocial: e.target.checked,
                    })
                  }
                  style={{ cursor: 'pointer', width: 18, height: 18 }}
                />
                <span style={{ fontWeight: 600 }}>
                  Habilitar Ingresso Social
                </span>
              </label>
            </div>

            {/* Valor Extra - Toggle e Campos */}
            <div
              style={{
                borderTop: '1px solid #f1f5f9',
                paddingTop: 14,
                marginTop: 14,
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                <input
                  type="checkbox"
                  checked={editForm.habilitarExtra}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      habilitarExtra: e.target.checked,
                    })
                  }
                  style={{
                    cursor: 'pointer',
                    width: 18,
                    height: 18,
                  }}
                />
                <span style={{ fontWeight: 600 }}>
                  Adicionar Valor Extra (Cinema, Transporte, etc)
                </span>
              </label>

              {/* Campos do Valor Extra - Com visual destacado */}
              {editForm.habilitarExtra && (
                <div
                  style={{
                    gap: 12,
                    flexDirection: 'column',
                    display: 'flex',
                    padding: '14px',
                    background: '#fff5f7',
                    borderRadius: 8,
                    border: '2px solid #ff2d6c',
                    borderLeft: '4px solid #ff2d6c',
                  }}
                >
                  <label style={styles.label}>
                    Nome do Extra
                    <input
                      value={editForm.nomeExtra}
                      onChange={(e) =>
                        setEditForm({ ...editForm, nomeExtra: e.target.value })
                      }
                      style={styles.input}
                      placeholder="ex: Ingresso Cinema"
                    />
                  </label>
                  <label style={styles.label}>
                    Descrição / Justificativa
                    <input
                      value={editForm.descricaoExtra}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          descricaoExtra: e.target.value,
                        })
                      }
                      style={styles.input}
                      placeholder="ex: Sessão exclusiva no cinema com pipoca"
                    />
                  </label>
                  <label style={styles.label}>
                    Valor Extra (R$)
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.valorExtra}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          valorExtra: parseFloat(e.target.value) || 0,
                        })
                      }
                      style={styles.input}
                      placeholder="25.00"
                    />
                  </label>
                  {editForm.nomeExtra && editForm.valorExtra > 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        color: '#475569',
                        padding: '8px 0',
                        borderTop: '1px solid #e2e8f0',
                      }}
                    >
                      <strong>Preview:</strong> R${' '}
                      {parseFloat(editForm.amount || 0) +
                        parseFloat(editForm.valorExtra || 0)}{' '}
                      (R$ {editForm.amount} + R$ {editForm.valorExtra} extra)
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setModalEditar(null)}
                style={styles.btnSecondary}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={salvando}
                style={styles.btnPrimary}
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {/* Modal: Editar Horário */}
      {modalEditarDia && (
        <ModalShell
          title="Editar horário"
          onClose={() => setModalEditarDia(null)}
        >
          <form
            onSubmit={salvarEditarDia}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <label style={styles.label}>
              Data e horário
              <input
                required
                type="datetime-local"
                style={styles.input}
                value={editDiaForm.dataInicio}
                onChange={(e) =>
                  setEditDiaForm({ ...editDiaForm, dataInicio: e.target.value })
                }
              />
            </label>
            <label style={styles.label}>
              Vagas disponíveis{' '}
              <span
                style={{
                  fontWeight: 400,
                  textTransform: 'none',
                  fontSize: 10,
                  color: '#94a3b8',
                }}
              >
                (deixe vazio = ilimitado)
              </span>
              <input
                type="number"
                min={1}
                style={styles.input}
                value={editDiaForm.vagas}
                placeholder="Ilimitado"
                onChange={(e) =>
                  setEditDiaForm({ ...editDiaForm, vagas: e.target.value })
                }
              />
            </label>
            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setModalEditarDia(null)}
                style={styles.btnSecondary}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={salvando}
                style={styles.btnPrimary}
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {/* Modal: Novo Horário */}
      {modalDia && (
        <ModalShell title="Adicionar horário" onClose={() => setModalDia(null)}>
          <form
            onSubmit={adicionarDia}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <label style={styles.label}>
              Data e horário
              <input
                required
                type="datetime-local"
                style={styles.input}
                value={novoDia.dataInicio}
                onChange={(e) =>
                  setNovoDia({ ...novoDia, dataInicio: e.target.value })
                }
              />
            </label>
            <label style={styles.label}>
              Vagas disponíveis{' '}
              <span
                style={{
                  fontWeight: 400,
                  textTransform: 'none',
                  fontSize: 10,
                  color: '#94a3b8',
                }}
              >
                (deixe vazio = ilimitado)
              </span>
              <input
                type="number"
                min={1}
                style={styles.input}
                value={novoDia.vagas ?? ''}
                placeholder="Ilimitado"
                onChange={(e) =>
                  setNovoDia({
                    ...novoDia,
                    vagas:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </label>
            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setModalDia(null)}
                style={styles.btnSecondary}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={salvando}
                style={styles.btnPrimary}
              >
                {salvando ? 'Salvando...' : 'Adicionar'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </AdminLayout>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 12,
  },
  h1: { fontSize: 22, fontWeight: 900, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: 13, color: '#94a3b8', margin: '4px 0 0' },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: 20,
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 4px rgba(0,0,0,.04)',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '9px 18px',
    background: '#ff2d6c',
    color: '#fff',
    border: 'none',
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    letterSpacing: '0.05em',
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '9px 18px',
    background: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  btnIcone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    border: '1px solid #f1f5f9',
    borderRadius: 10,
    cursor: 'pointer',
    color: '#64748b',
    width: 32,
    height: 32,
    flexShrink: 0,
  },
  btnClose: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    color: '#64748b',
    width: 30,
    height: 30,
    flexShrink: 0,
  },
  btnLinkAzul: {
    fontSize: 12,
    color: '#2563eb',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 700,
    padding: 0,
  },
  btnLinkVermelho: {
    fontSize: 12,
    color: '#dc2626',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 700,
    padding: 0,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    color: '#94a3b8',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    color: '#334155',
    borderBottom: '1px solid #f8fafc',
  },
  tr: { transition: 'background .1s' },
  diaCard: {
    background: '#f8fafc',
    border: '1px solid #f1f5f9',
    borderRadius: 12,
    padding: 14,
  },
  pill: {
    fontSize: 11,
    fontWeight: 700,
    color: '#475569',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 99,
    padding: '3px 10px',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: 16,
    boxSizing: 'border-box',
  },
  modal: {
    background: '#fff',
    borderRadius: 20,
    padding: 32,
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,.15)',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  modalTitle: { fontSize: 18, fontWeight: 900, color: '#0f172a', margin: 0 },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 11,
    fontWeight: 900,
    color: '#64748b',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  input: {
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 14,
    color: '#0f172a',
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  erro: {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '10px 16px',
    borderRadius: 10,
    marginBottom: 16,
    fontSize: 13,
  },
  feedback: {
    padding: '10px 16px',
    borderRadius: 10,
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 700,
  },
};
